/**
 * Libellés métier français pour les codes techniques renvoyés par l’API.
 * Toute valeur affichée à l’utilisateur doit passer par ces helpers.
 */

const AVAILABILITY_FR: Record<string, string> = {
  AVAILABLE_IN_REPORT: "Disponible dans le rapport",
  PARTIALLY_AVAILABLE: "Partiellement disponible",
  NOT_YET_COLLECTED: "Pas encore collecté",
  NOT_AVAILABLE_IN_REPORT: "Non disponible dans le rapport",
};

const COVERAGE_STATUS_FR: Record<string, string> = {
  available: "Disponible",
  to_validate: "À valider",
  absent: "Absent",
};

const INDICATOR_STATUS_FR: Record<string, string> = {
  publie: "Publié",
  a_valider: "À valider",
  sans_mesure: "Sans mesure",
};

const MEASURE_STATUS_FR: Record<string, string> = {
  publiee: "Publiée",
  PUBLIEE: "Publiée",
  brouillon: "Brouillon",
  saisie: "Saisie",
  controlee: "Contrôlée",
  validee: "Validée",
  a_revoir: "À revoir",
  rejetee: "Rejetée",
};

const QUALITY_FR: Record<string, string> = {
  bonne: "Bonne",
  moyenne: "Moyenne",
  faible: "Faible",
  a_revoir: "À revoir",
};

function lookup(map: Record<string, string>, value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  if (map[value]) return map[value]!;
  const lower = value.toLowerCase();
  if (map[lower]) return map[lower]!;
  return humanizeCode(value);
}

/** Fallback lisible pour un code inconnu (évite d’afficher du SCREAMING_SNAKE anglais). */
function humanizeCode(value: string): string {
  const spaced = value.replace(/_/g, " ").trim().toLowerCase();
  if (!spaced) return "—";
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function formatAvailabilityLabel(value: string | null | undefined): string {
  return lookup(AVAILABILITY_FR, value);
}

export function formatCoverageStatusLabel(value: string | null | undefined): string {
  return lookup(COVERAGE_STATUS_FR, value);
}

export function formatIndicatorStatusLabel(value: string | null | undefined): string {
  return lookup(INDICATOR_STATUS_FR, value);
}

export function formatMeasureStatusLabel(value: string | null | undefined): string {
  return lookup(MEASURE_STATUS_FR, value);
}

export function formatQualityLabel(value: string | null | undefined): string {
  return lookup(QUALITY_FR, value);
}

/** Tooltip descriptif pour une cellule de matrice de couverture avec mesures. */
export function formatCoverageCellTooltip(args: {
  readonly row: string;
  readonly col: string;
  readonly value: number;
  readonly status?: string;
}): string | undefined {
  if (args.value <= 0) return undefined;
  const statusLabel = formatCoverageStatusLabel(args.status ?? "available");
  const countLabel = args.value === 1 ? "1 mesure publiée" : `${args.value} mesures publiées`;
  return [
    `${countLabel} pour l’indicateur « ${args.row} ».`,
    `Périmètre : ${args.col}.`,
    `Statut : ${statusLabel} — des observations sont disponibles pour l’analyse.`,
  ].join(" ");
}
